import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "../../api/client";
import { ADMIN } from "../../constants/testIds";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { InitialsAvatar } from "../../components/shared/InitialsAvatar";
import { StatusBadge } from "../../components/shared/StatusBadge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Check, Flag, Clock, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";

export default function Approvals() {
    const nav = useNavigate();
    const [pending, setPending] = useState([]);
    const [variations, setVariations] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [resolving, setResolving] = useState(null);
    const [rAmount, setRAmount] = useState("");
    const [rNotes, setRNotes] = useState("");
    const [rApprove, setRApprove] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        const [p, v] = await Promise.all([
            api.get("/v1/timesheets/pending").then((r) => r.data),
            api.get("/v1/admin/variations", { params: { status: "unresolved" } }).then((r) => r.data),
        ]);
        setPending(p);
        setVariations(v);
        setSelected(new Set());
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggle = (id) => {
        setSelected((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };
    const toggleAll = () => {
        if (selected.size === pending.length) setSelected(new Set());
        else setSelected(new Set(pending.map((t) => t.id)));
    };

    const bulkApprove = async () => {
        if (selected.size === 0) return;
        setBusy(true);
        try {
            const { data } = await api.post("/v1/timesheets/bulk-approve", {
                timesheet_ids: Array.from(selected),
            });
            toast.success(`Approved ${data.approved.length} timesheet${data.approved.length === 1 ? "" : "s"} · Push sent.`);
            await load();
        } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };

    const openResolve = (v) => {
        setResolving(v);
        setRAmount("");
        setRNotes(v.note || "");
        setRApprove(true);
    };

    const submitResolve = async () => {
        if (!rNotes.trim()) return;
        setBusy(true);
        try {
            await api.post(`/v1/admin/variations/${resolving.entry_id}/resolve`, {
                amount: parseFloat(rAmount) || 0,
                notes: rNotes,
                approve_on_job: rApprove,
            });
            toast.success("Variation resolved.");
            setResolving(null);
            await load();
        } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };

    return (
        <div data-testid={ADMIN.approvalsRoot} className="space-y-8">
            <div>
                <div className="eyebrow">Admin</div>
                <h1 className="text-4xl font-extrabold tracking-[-0.03em] leading-none">Approvals</h1>
                <p className="text-muted mt-2 text-sm">
                    Shannon&apos;s Monday queue. Bulk-approve timesheets and resolve field variations.
                </p>
            </div>

            {/* Variations panel */}
            <section className="km-card p-6" data-testid={ADMIN.variationsList}>
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-md bg-[color:var(--km-red-l)] flex items-center justify-center">
                            <Flag size={16} className="text-[color:var(--km-red)]" />
                        </div>
                        <h2 className="text-xl font-extrabold tracking-[-0.03em]">Variations flagged in the field</h2>
                        <span className="km-chip km-chip-red num">{variations.length}</span>
                    </div>
                </div>
                {variations.length === 0 ? (
                    <div className="text-sm text-muted py-8 text-center">No unresolved variations. Nice.</div>
                ) : (
                    <ul className="divide-y divide-km">
                        {variations.map((v) => (
                            <li key={v.entry_id} data-testid={ADMIN.variationRow(v.entry_id)} className="py-4 flex items-start gap-4 flex-wrap">
                                <InitialsAvatar name={v.user?.name} initials={v.user?.initials} size={36} />
                                <div className="flex-1 min-w-[240px]">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-ink text-sm">{v.user?.name || "Unknown"}</span>
                                        {v.job && (
                                            <>
                                                <span className="text-muted text-xs">·</span>
                                                <span className="num text-xs font-bold text-[color:var(--km-navy)]">{v.job.job_number || "—"}</span>
                                                <span className="text-xs text-muted truncate">{v.job.address}</span>
                                            </>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-muted mt-1 flex items-center gap-2 num">
                                        <Clock size={11} />
                                        {(v.start_time || "").slice(0, 10)}
                                        <span>·</span>
                                        {(v.duration_hrs || 0).toFixed(1)}h
                                        <span>·</span>
                                        {v.work_type}
                                    </div>
                                    {v.note && (
                                        <div className="mt-2 text-sm text-ink bg-[color:var(--surface-2)] rounded-md px-3 py-2">
                                            &ldquo;{v.note}&rdquo;
                                        </div>
                                    )}
                                </div>
                                <Button
                                    onClick={() => openResolve(v)}
                                    size="sm"
                                    className="gap-2 bg-[color:var(--km-navy)] hover:bg-[color:var(--km-navy-d)]"
                                >
                                    Resolve <ArrowRight size={13} />
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Pending timesheets */}
            <section className="km-card p-6">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-md bg-[color:var(--km-navy-l)] flex items-center justify-center">
                            <Check size={16} className="text-[color:var(--km-navy)]" />
                        </div>
                        <h2 className="text-xl font-extrabold tracking-[-0.03em]">Pending timesheets</h2>
                        <span className="km-chip km-chip-navy num">{pending.length}</span>
                    </div>
                    <Button
                        data-testid={ADMIN.approvalBulk}
                        onClick={bulkApprove}
                        disabled={selected.size === 0 || busy}
                        className="gap-2 bg-[color:var(--km-green)] hover:bg-[color:var(--km-green-d)]"
                    >
                        <Check size={14} /> Approve {selected.size} selected
                    </Button>
                </div>
                {pending.length === 0 ? (
                    <div className="text-sm text-muted py-8 text-center">Nothing waiting. Inbox zero.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-km text-[11px] uppercase tracking-[0.12em] text-muted">
                                    <th className="px-3 py-3 w-8">
                                        <input
                                            type="checkbox"
                                            checked={selected.size === pending.length && pending.length > 0}
                                            onChange={toggleAll}
                                        />
                                    </th>
                                    <th className="px-3 py-3 font-medium">Staff</th>
                                    <th className="px-3 py-3 font-medium">Week</th>
                                    <th className="px-3 py-3 font-medium text-right">Std</th>
                                    <th className="px-3 py-3 font-medium text-right">OT</th>
                                    <th className="px-3 py-3 font-medium text-right">KMs</th>
                                    <th className="px-3 py-3 font-medium">Submitted</th>
                                    <th className="px-3 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pending.map((t) => (
                                    <tr key={t.id} data-testid={ADMIN.approvalRow(t.id)} className="border-b border-km hover:bg-[color:var(--surface-2)]">
                                        <td className="px-3 py-3">
                                            <input
                                                type="checkbox"
                                                data-testid={ADMIN.approvalCheckbox(t.id)}
                                                checked={selected.has(t.id)}
                                                onChange={() => toggle(t.id)}
                                            />
                                        </td>
                                        <td className="px-3 py-3">
                                            <div className="flex items-center gap-2">
                                                <InitialsAvatar name={t.user?.name} initials={t.user?.initials} size={28} />
                                                <span className="text-ink">{t.user?.name || "Unknown"}</span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-3 num">{t.week_start}</td>
                                        <td className="px-3 py-3 num text-right">{(t.totals?.std_hrs || 0).toFixed(1)}</td>
                                        <td className="px-3 py-3 num text-right">
                                            <span className={t.totals?.ot_hrs > 0 ? "text-[color:var(--km-red)]" : ""}>
                                                {(t.totals?.ot_hrs || 0).toFixed(1)}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3 num text-right">{(t.totals?.km || 0).toFixed(0)}</td>
                                        <td className="px-3 py-3 text-xs text-muted num">
                                            {t.submitted_at
                                                ? new Date(t.submitted_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })
                                                : "—"}
                                        </td>
                                        <td className="px-3 py-3 text-right">
                                            <button
                                                onClick={() => nav(`/timesheet?week=${t.week_start}&user_id=${t.user_id}`)}
                                                className="text-xs text-[color:var(--km-navy)] hover:underline"
                                            >
                                                Review →
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Resolve variation dialog */}
            <Dialog open={!!resolving} onOpenChange={(o) => !o && setResolving(null)}>
                <DialogContent data-testid={ADMIN.resolveDialog}>
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-extrabold tracking-[-0.03em]">Resolve variation</DialogTitle>
                    </DialogHeader>
                    {resolving && (
                        <div className="space-y-4">
                            <div className="text-sm">
                                <div className="text-ink font-medium">{resolving.user?.name}</div>
                                {resolving.job && (
                                    <div className="text-xs text-muted">
                                        <span className="num text-[color:var(--km-navy)] font-bold">{resolving.job.job_number}</span>
                                        {" · "}{resolving.job.address}
                                    </div>
                                )}
                                {resolving.note && (
                                    <div className="mt-2 text-xs bg-[color:var(--surface-2)] rounded px-3 py-2">
                                        &ldquo;{resolving.note}&rdquo;
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.12em] text-muted">$ amount to bill client</label>
                                <Input
                                    data-testid={ADMIN.resolveAmount}
                                    type="number" step="0.01" min="0" className="num"
                                    value={rAmount} onChange={(e) => setRAmount(e.target.value)}
                                    placeholder="0.00"
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.12em] text-muted">Resolution notes (required)</label>
                                <Textarea
                                    data-testid={ADMIN.resolveNotes}
                                    rows={3} value={rNotes} onChange={(e) => setRNotes(e.target.value)}
                                    placeholder="What did the client agree to?"
                                />
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={rApprove} onChange={(e) => setRApprove(e.target.checked)} />
                                Also add as an approved Variation on the job
                            </label>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResolving(null)}>Cancel</Button>
                        <Button
                            data-testid={ADMIN.resolveSubmit}
                            onClick={submitResolve}
                            disabled={!rNotes.trim() || busy}
                        >
                            Resolve
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
